import React, { forwardRef } from "react";
import { css } from "goober";

import { classNames } from "@/util/class-names.mjs";

export const CardContainer = () => css`
  container-type: inline-size;
  container-name: content-card;

  /* where is handy because it has zero specificity */
  :where(& .card-frame) {
    --padd: var(--sp-6);
    --card-radii: var(--br-lg);
    --card-inset-border: var(--shade3-25);
    --card-surface: var(--shade8);
    --card-surface2: var(--shade7);
    position: relative;
    background: var(--card-surface);
    border-radius: var(--card-radii);
    contain: paint; /* prevents painting from spilling out, ex hover bg corners */

    &::after {
      content: "";
      position: absolute;
      inset: 0;
      border: 1px solid var(--card-inset-border);
      border-radius: var(--card-radii);
      z-index: 2;
      pointer-events: none;
    }

    /* These should probably just be :has */
    &.with-header {
      .card-contents {
        padding-block-start: unset;
      }
    }
    &.with-subtitle {
      .card-header {
        padding-block-end: var(--sp-4);
        border-bottom: 1px solid var(--card-inset-border);
      }
    }
    &.with-action {
      .card-footer {
        padding-inline-end: var(--sp-2_5);
      }
    }
    &.with-controls {
      .card-header {
        padding-inline-end: var(--sp-4);
      }
    }
  }

  :where(& .card-contents) {
    padding: var(--padd);
  }

  :where(.card-header) {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-inline: var(--padd);
    padding-block: var(--sp-5) var(--sp-2);
    color: var(--shade0);
    border-top-left-radius: var(--card-radii);
    border-top-right-radius: var(--card-radii);
    z-index: 2;

    .card-header--controls {
      display: flex;
      gap: var(--sp-2);

      svg {
        font-size: var(--sp-5);
        color: var(--shade3);
      }
    }
  }

  .card-surface-2 {
    width: 100%;
    color: var(--shade0-50);
    background: var(--shade7);
  }

  .card-meta {
    display: grid;
    gap: var(--sp-1);
  }

  .card-title {
    a:hover {
      text-decoration: underline;
    }
  }

  .card-subtitle {
    display: block;
    color: var(--shade0-75);
  }

  .card-footer-text {
    text-align: center;
  }

  :where(.card-footer-button) {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-inline: var(--padd);
    padding-block: var(--sp-1_5);
    border: none;
    border-bottom-left-radius: var(--card-radii);
    border-bottom-right-radius: var(--card-radii);
    box-shadow: inset 0 1px 0 var(--card-inset-border);
    cursor: pointer;

    &:hover {
      background: var(--shade6-75);
      color: var(--shade0-75);
    }

    svg {
      width: var(--sp-4_5);
      height: var(--sp-4_5);
      fill: currentColor;
    }
  }
`;

export type CardProps = {
  as?: keyof JSX.IntrinsicElements;
  padding?: number | string;
  children?: React.ReactNode;
  className?: string;
  classNameOuter?: string;
  style?: React.CSSProperties;
  title?: string | React.ReactNode;
  titleLink?: string;
  titleTag?: keyof JSX.IntrinsicElements;
  subtitle?: string | React.ReactNode;
  headerControls?: React.ReactNode;
  footerButtonText?: string;
  footerButtonIcon?: React.ReactNode;
  footerButtonFn?(): void;
  footerButtonLink?: string;
  loading?: boolean;
  loadingClassName?: string;
  onClick?: () => void;
};

const Card: React.ForwardRefRenderFunction<HTMLElement, CardProps> = (
  {
    as = "section",
    padding,
    children,
    className,
    classNameOuter,
    style,
    title,
    titleLink,
    titleTag = "h3",
    subtitle,
    headerControls,
    footerButtonText,
    footerButtonIcon,
    footerButtonFn,
    footerButtonLink,
    loading,
    loadingClassName,
    ...rest // Try to avoid, mostly useful for data attrs
  }: CardProps,
  ref,
) => {
  function handleFooterClick() {
    if (typeof footerButtonFn === "function") {
      footerButtonFn();
    }
  }

  const RootElem = as as unknown as (
    // nice as.
    e: JSX.IntrinsicElements[typeof as],
  ) => JSX.Element;

  if (loading) {
    const loadingStyles: React.CSSProperties = { height: style?.height || 300 };

    // Loading class should take precedence over applied styles. Otherwise, apply
    // loading style prop to maintain backwards compatibility.
    const loadingStyleProp = !loadingClassName ? loadingStyles : undefined;

    return (
      <RootElem
        {...rest}
        {...classNames(CardContainer(), classNameOuter)}
        ref={ref}
      >
        <div
          style={{ ...style, padding: 0 }}
          {...classNames("card-frame", "card-contents", className)}
        >
          <div
            style={loadingStyleProp}
            {...classNames("loading-row", loadingClassName)}
          />
        </div>
      </RootElem>
    );
  }

  if (!title && !headerControls && !footerButtonText && !footerButtonFn) {
    return (
      <RootElem
        {...rest}
        {...classNames(CardContainer(), classNameOuter)}
        ref={ref}
      >
        <div
          style={{ ...style, padding }}
          {...classNames("card-frame", "card-contents", className)}
        >
          {children}
        </div>
      </RootElem>
    );
  }

  const TitleElem = titleTag as unknown as (
    // nice as.
    e: JSX.IntrinsicElements[typeof titleTag],
  ) => JSX.Element;

  return (
    <RootElem
      ref={ref}
      {...classNames(CardContainer(), classNameOuter)}
      {...rest}
    >
      <div
        {...classNames(
          "card-frame",
          !title && className,
          headerControls && "with-controls",
          title && "with-header",
          subtitle && "with-subtitle",
        )}
      >
        {title || headerControls ? (
          <header {...classNames("card-header")}>
            {title && titleLink ? (
              <div className="card-meta">
                <TitleElem className="card-title type-title--bold">
                  <a href={titleLink}>{title}</a>
                </TitleElem>
                {subtitle && (
                  <span className="card-subtitle type-caption">{subtitle}</span>
                )}
              </div>
            ) : title ? (
              <div className="card-meta">
                <TitleElem className="card-title type-title--bold">
                  {title}
                </TitleElem>
                {subtitle && (
                  <span className="card-subtitle type-caption">{subtitle}</span>
                )}
              </div>
            ) : null}

            {headerControls && (
              <div className="card-header--controls">{headerControls}</div>
            )}
          </header>
        ) : null}
        <div
          style={{ ...style, padding }}
          {...classNames("card-contents", title && className)}
        >
          {children}
        </div>
        {footerButtonText ? (
          <footer className="card-footer">
            {footerButtonFn ? (
              <button
                className="card-footer-button card-surface-2"
                onClick={handleFooterClick}
              >
                <span className="type-callout--semi">{footerButtonText}</span>
                {footerButtonIcon && footerButtonIcon}
              </button>
            ) : footerButtonLink ? (
              <a
                href={footerButtonLink}
                className="card-footer-button card-footer-text card-surface-2"
              >
                <span className="type-callout--semi">{footerButtonText}</span>
                {footerButtonIcon && footerButtonIcon}
              </a>
            ) : (
              <span className="card-footer-text type-callout--semi card-surface-2">
                {footerButtonText}
              </span>
            )}
          </footer>
        ) : null}
      </div>
    </RootElem>
  );
};

const v = forwardRef(Card);
export { v as Card };
export default v;
